/**
 * El velo con el recorte sobre el elemento del paso.
 *
 * **No captura clics.** El velo entero es `pointer-events-none`, así que el
 * usuario puede seguir pulsando el panel con la guía abierta —incluido el
 * botón que el paso le está señalando, que es justo lo que pide un paso
 * `advanceOn: 'click-target'`—. Un velo que bloquea convierte la guía en un
 * peaje; éste sólo atenúa lo que no toca.
 *
 * El recorte es una sombra de 9999px hacia afuera desde el rectángulo del
 * objetivo: un solo nodo, sin cuatro tiras que cuadrar en cada scroll.
 *
 * Sin blur, a diferencia del velo de `Modal`: el `MASTER.md` reserva el
 * desenfoque para los diálogos, y aquí el resto de la pantalla tiene que
 * seguir siendo legible mientras se explica.
 */

import { motion, useReducedMotion } from 'framer-motion';
import { backdropFade, duration, easing } from '@/lib/motion';
import type { TargetRect } from './useTargetRect';

const PADDING = 6;

interface TourSpotlightProps {
  rect: TargetRect | null;
}

export function TourSpotlight({ rect }: TourSpotlightProps) {
  // El recorte se desplaza por la pantalla al cambiar de paso, que es
  // justo el movimiento que molesta a quien pide menos animación. La regla
  // CSS de `index.css` no lo cubre: framer-motion no anima con transiciones
  // CSS, así que hay que preguntar aquí.
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      variants={backdropFade}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="pointer-events-none fixed inset-0 z-[130]"
      aria-hidden="true"
    >
      {rect ? (
        <motion.div
          className="absolute rounded-lg ring-2 ring-background"
          initial={false}
          animate={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
          }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: duration.base, ease: easing.emphasized }
          }
          style={{ boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.55)' }}
        />
      ) : (
        // Paso centrado: sin objetivo que recortar, el velo es liso.
        <div className="absolute inset-0 bg-black/55" />
      )}
    </motion.div>
  );
}
